import ko from 'knockout';
import uploadValidation from '@/App/uploadValidation';
import logger from '@/Vendor/inv-logger/logger';
import ResponseModel from '@/App/Models/responseModel';
import uploadRepository from '@/App/Repositories/uploadRepository';
import responseRepository from '@/App/Repositories/responseRepository';
import ValidationRulesService from '@/Vendor/inv-validation/validationRulesService';
import resourceHelper from '@/Vendor/inv-resource-helper/resourceHelper';
import fileUpload from '@/Vendor/inv-file-upload/fileUpload';
import contextData from '@/App/contextData';
import constants from '@/App/constants';
import uploadManager from '@/Vendor/inv-file-upload/utils/uploadManager';

import 'knockout.validation';
import '@/App/Bindings/showModalBindings';
import '@/App/Bindings/beforeUnloadBinding';
import '@/App/Bindings/ariaInvalid';

import template from './responseForm.html';

export function ResponseForm(params) {

  var self = this;

  self.languageCode = contextData.languageCode;

  self.helpLinkTitle = resourceHelper.getString('HelpLinkTitle');
  self.confirmResponseModalCloseButtonLabel = resourceHelper.getString('ConfirmResponseModalCloseButton');
  self.leaveWarning = resourceHelper.getString('ChangesMayNotBeSaved');

  self.responseType = params.responseMetadata.responseParentType;
  self.responseTypes = constants.responseParentType;

  self.responsePublicReference = params.responsePublicReference;

  self.responseValidationRules = contextData.responseValidationRules;
  self.response = new ResponseModel(self.responseValidationRules);

  var validationSettings = {
    insertMessages: false,
    decorateElement: true,
    errorElementClass: 'has-error'
  };

  ko.validation.init(validationSettings, true);

  // Uploads use custom validation but the response uses standard knockout validation
  var validationRulesService = new ValidationRulesService(self.response);
  validationRulesService.applyValidation(self.responseValidationRules);

  self.uploads = ko.observableArray([]);
  self.uploadClientErrorResourceObject = ko.observable(null);
  self.allUploadsComplete = uploadManager.allUploadsComplete;

  self.responseConfirmationText = ko.observable('');
  self.responseConfirmationTitleTemplate = ko.observable('');
  self.responseConfirmationTitle = ko.computed(function () {
    return self.responseConfirmationTitleTemplate().replace('{uploadedFilesCount}', self.uploads().length);
  });

  self.emailAddressIsVisible = ko.observable(true); // Flags if the email address is a visible input or a hidden input

  self.showSubmitPrompt = ko.observable(false); // Flags if the response summary and T&Cs modal is displayed
  self.formSubmitted = ko.observable(false); // Flags if submit Ajax request in progress

  self.areUploadedFilesLoaded = ko.observable(false); // Flags if retrieve upload files details Ajax request completed

  self.isAllDataLoaded = ko.computed(function () { // Flags if all data for response is loaded
    return self.areUploadedFilesLoaded();
  });

  self.hasPendingUploads = ko.pureComputed(function () {
    return uploadValidation.getValidationResourceKey(self.uploads()) === 'UploadsStillInProgress';
  });

  self.allFilesUploaded = ko.pureComputed(function () { // Flags if there are uploads and they are all complete
    return self.uploads().length > 0 && !self.hasPendingUploads();
  });

  self.addFileUploads = function (fileList) {
    var newUploads;

    var resourceObject = uploadValidation.getValidationResourceKeyForNewUploads(fileList, self.uploads());
    self.uploadClientErrorResourceObject(resourceObject);

    if (resourceObject) {
      var validationFailureText = getLocalizedString(resourceObject);
      logger.error('UploadFailure', validationFailureText);
      return;
    }

    newUploads = uploadRepository.addUploads(fileList, self.responsePublicReference, self.addUploadsFailed);
    self.uploads.unshift(...newUploads); // Add newest items to the top of the list
  };

  self.addUploadsFailed = function (e) {
    if (e.errorMessages) {
      self.serverErrors(e.errorMessages);
      return;
    }

    const resourceKey = e.status === 0 ? 'UnhandledErrorOffline' : 'UnhandledError';
    logger.error(resourceKey, 'An unexpected error occurred while attempting to add file uploads.', e);
  };

  /**
   * Calls the API to remove an upload. The API call could fail (if offline for example) and in this
   * scenario the upload must not be removed from the UI as this will invalidate the client-side
   * validation as the upload still exists.
   * @param {UploadModel} upload
   */
  self.removeUpload = function (upload) {
    upload.setIsBeingRemoved(true);

    uploadRepository.deleteUpload(upload)
      .then(() => {
        self.uploads.remove(upload);
      })
      .catch(function (jqXhr) {
        upload.setIsBeingRemoved(false); // Re-instate the upload exactly as it was before removal attempt

        if (!jqXhr.errorHasBeenLogged) {
          const resourceKey = jqXhr.status === 0 ? 'UnhandledErrorOffline' : 'UnhandledError';
          logger.error(resourceKey, 'Failed to delete the file upload.', jqXhr);
        }
      });
  };

  self.confirmSubmit = function () {

    let someValidationErrors = false;
    var validationFailureResourceObject = uploadValidation.getValidationResourceKey(self.uploads());

    if (validationFailureResourceObject) {
      someValidationErrors = true;
      self.uploadClientErrorResourceObject(validationFailureResourceObject);
    }

    if (self.response.clientErrors().length > 0) {
      someValidationErrors = true;
      self.response.clientErrors.showAllMessages(true);
    }

    if (someValidationErrors) {
      return;
    }

    self.uploadClientErrorResourceObject(null);
    self.showSubmitPrompt(true);
  };

  self.submitForm = function () {

    self.showSubmitPrompt(false);
    self.formSubmitted(true);

    var responseDetails = ko.toJS(self.response);

    return uploadRepository.updateUploads(self.uploads())
      .then(function () {
        return responseRepository.completeResponse(self.responsePublicReference, responseDetails);
      })
      .then(function () {
        navigateToResponseCompletePage(self.responsePublicReference);
      })
      .catch(function (jqXhr) {
        self.formSubmitted(false);

        logger.logAndThrowUntranslatedError(
          'An error occurred on submitting a response. Please try again later.',
          'Unable to submit a response',
          jqXhr);
      });
  };

  function getResponseUploads(params) {
    return uploadRepository.getUploadsByResponsePublicReference(params.responsePublicReference)
      .then(function (uploadList) {
        ko.utils.arrayPushAll(self.uploads, uploadList);
        self.areUploadedFilesLoaded(true);
      })
      .catch(function (jqXhr) {
        logger.logAndThrowUntranslatedError(
          'An error occurred on loading the response uploads. Please try again later.',
          'Unable to get the response uploads',
          jqXhr);
      });
  }

  function navigateToResponseCompletePage(responsePublicReference) {
    window.location.href = '/' + self.languageCode + '/response/' + responsePublicReference;
  }

  function getLocalizedString(resTextObject) {
    var value = resourceHelper.getString(resTextObject.key);
    value = resourceHelper.replacePlaceholders(value, resTextObject.params);

    return value;
  }

  function initResponseForm(params) {
    var responseMetadata = params.responseMetadata;
    var responseDetails = params.responseDetails;

    setResponseConfirmationMessage(responseMetadata);

    // If we have the email address because this was a request to an email then use a hidden field for this value
    if (responseDetails.email) {
      self.response.setEmail(responseDetails.email);
      self.emailAddressIsVisible(false);
    }

    return Promise.resolve(params);
  }

  function setResponseConfirmationMessage(responseMetadata) {
    var responseConfirmationMessageTemplate;

    if (responseMetadata.responseParentType === constants.responseParentType.request) {
      responseConfirmationMessageTemplate = resourceHelper.getString('ConfirmRequestResponseModalMessage');
      responseConfirmationMessageTemplate = responseConfirmationMessageTemplate
        .replace('{requestorFullName}', responseMetadata.requestorFullName);
    } else if (self.responseType === constants.responseParentType.appeal) {
      responseConfirmationMessageTemplate = resourceHelper.getString('ConfirmAppealResponseModalMessage');
    }

    self.responseConfirmationTitleTemplate(responseConfirmationMessageTemplate);
    self.responseConfirmationText(responseMetadata.responseConfirmationText);
  }


  function initFileUploadControl(params) {
    var responseMetadata = params.responseMetadata;

    if (responseMetadata.responseParentType === constants.responseParentType.request) {
      self.maximumUploadSize = contextData.portalSettings.maximumRequestUploadSize;
      self.maximumFilesUploads = contextData.portalSettings.maximumRequestFilesUploads;

    } else if (responseMetadata.responseParentType === constants.responseParentType.appeal) {
      self.maximumUploadSize = contextData.portalSettings.maximumAppealUploadSize;
      self.maximumFilesUploads = contextData.portalSettings.maximumAppealFilesUploads;
    }

    self.fileUploadSelectorOptions = {
      maximumUploadSize: self.maximumUploadSize,
      addFileUploadsCallback: self.addFileUploads,
      maximumFilesUploads: self.maximumFilesUploads
    };

    uploadValidation.init({
      maxFileSize: self.maximumUploadSize,
      fileNumberLimit: self.maximumFilesUploads,
      blacklistedFileTypes: contextData.portalSettings.blacklistedFileTypes
    });

    fileUpload.init({
      languageCode: self.languageCode,
      fileUploadServiceRootUrl: contextData.portalSettings.fileUploadServiceRootUrl,
      maximumChunkSize: contextData.portalSettings.maximumChunkSize,
      maximumConcurrentUploads: contextData.portalSettings.maximumConcurrentUploads,
      maximumFilesUploads: self.maximumFilesUploads
    });

    return Promise.resolve(params);
  }

  initResponseForm(params);
  initFileUploadControl(params);
  getResponseUploads(params);
}

// The default export returns the component details object to register with KO
export default { viewModel: ResponseForm, template: template };
